<script setup>
import ExportCustomersAudienceAction from '@frontJs/components/actions/projectManagement/sma/ExportCustomersAudienceAction.vue';
import SynchronizeAudienceAction from '@frontJs/components/actions/projectManagement/sma/SynchronizeAudienceAction.vue';
import ArrowsClockwiseIcon from '@iconify-vue/ph/arrows-clockwise';
import DownloadSimpleIcon from '@iconify-vue/ph/download-simple';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import LoadingSpinner from '@js/components/LoadingSpinner.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ContainerRoot from '@js/components/containers/ContainerRoot.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
    globalData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            header: () => 'Nom',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                    length: 160,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            header: () => 'Type',
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.type.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'total',
            header: () => 'Total',
            cell: (props) =>
                props.row.original.total
                    ? h(NumberCell, {
                          number: props.row.original.total,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'synchronizedAt',
            header: () => 'Synchronisé le',
            cell: (props) =>
                props.row.original.synchronizedAt
                    ? h(TextCell, {
                          text: formatDate(props.row.original.synchronizedAt),
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.sma.audience',
                                    {
                                        tenant: tenant.id,
                                        facebookAdsAudienceId:
                                            props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                            }),
                            h(
                                SynchronizeAudienceAction,
                                {
                                    facebookAdsAudience: props.row.original,
                                    tenant,
                                    callback: updateData,
                                },
                                {
                                    button: ({ action, loading }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            title: 'Synchroniser',
                                            icon: ArrowsClockwiseIcon,
                                            callback: action,
                                            loading,
                                            disabled:
                                                !props.row.original
                                                    .canBeSynchronized,
                                        }),
                                },
                            ),
                            h(
                                ExportCustomersAudienceAction,
                                {
                                    facebookAdsAudience: props.row.original,
                                    tenant,
                                },
                                {
                                    button: ({ action, loading }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            type: 'secondary',
                                            title: 'Exporter les clients',
                                            icon: DownloadSimpleIcon,
                                            callback: action,
                                            loading,
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.sma.postAudiences', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <LoadingSpinner v-if="loading" />

    <template v-else>
        <ContainerRoot
            v-if="globalData.accountId && globalData.accountId !== ''"
            class="wysiwyg mb-4"
        >
            <ul>
                <li>
                    Lien vers conditions générales des audiences personnalisées
                    basées sur des listes clients (à accepter avant de
                    créer/modifier/synchroniser une audience) :
                    <ul>
                        <li>
                            <a
                                :href="
                                    'https://business.facebook.com/ads/manage/customaudiences/tos/?act=' +
                                    globalData.accountId
                                "
                                target="_blank"
                            >
                                https://business.facebook.com/ads/manage/customaudiences/tos/?act={{
                                    globalData.accountId
                                }}
                            </a>
                        </li>
                    </ul>
                </li>
            </ul>
        </ContainerRoot>

        <DataTableLayout
            :table="table"
            :count="count"
            :global-filter="globalFilter"
            :filters="filters"
            :available-filters="availableFilters"
            :set-filters="setFilters"
            :set-global-filter="setGlobalFilter"
        >
            <template #actions>
                <ActionButton
                    size="small"
                    :href="
                        route('front.projectManagement.sma.audience', {
                            tenant: tenant.id,
                        })
                    "
                    :left-icon="PlusIcon"
                >
                    Ajouter
                </ActionButton>
            </template>
        </DataTableLayout>
    </template>
</template>
